import { useEffect, useRef, useState } from 'react';

import { Link as RouterLink } from "react-router-dom";

import {
    chakra, shouldForwardProp,
    Center, Flex, Grid, IconButton,
    Slider, SliderTrack, SliderFilledTrack,
    SliderThumb, Tooltip, Link, Image, DarkMode
} from '@chakra-ui/react';
import { CloseIcon, ExternalLinkIcon } from '@chakra-ui/icons';

import { FaFileAlt } from "react-icons/fa";

import {
    motion, AnimatePresence, isValidMotionProp
} from 'framer-motion';

import { SwitchColorModeButton } from "../parts/ToggleColorModeButton";

import imgAvatar from "../images/portfolio/banner-avatar.webp";
import vidAvatar from "../videos/avatar.mp4";

import imgPt from "../images/portfolio/banner-pt.jpg";
import vidPt from "../videos/pt.mp4";

import imgF from "../images/portfolio/banner-f.webp";
import vidF from "../videos/f.mp4";

import imgTaiko from "../images/portfolio/banner-taiko.webp";
import vidTaiko from "../videos/taiko.mp4";

import imgBox from "../images/box/box.webp";
import vidBox from "../videos/box-2.mp4";

import imgSplatoon from "../images/portfolio/banner-splatoon.webp";
import vidSplatoon from "../videos/splatoon.mp4";

import imgAR from "../images/portfolio/banner-ar.webp";

import imgSara from "../images/portfolio/banner-sara.webp";
import vidSara from "../videos/sara.mp4";
import { Helmet } from 'react-helmet';

const MotionBox = chakra(motion.div, {
    shouldForwardProp: prop => isValidMotionProp(prop) || prop === 'children' || shouldForwardProp(prop)
});
const MotionText = chakra(motion.p, {
    shouldForwardProp: prop => isValidMotionProp(prop) || prop === 'children' || shouldForwardProp(prop),
    baseStyle: {
        color: "#ffffff"
    }
});
const MotionHeading = chakra(motion.h1, {
    shouldForwardProp: prop => isValidMotionProp(prop) || prop === 'children' || shouldForwardProp(prop)
})

const StyledLink = ({ isExternal = false, href = "", children, ...linkProps }) => {
    const normalizedHref = href.replace("https://shiky.me", "");
    const fallbackText = href.replace(/^https?:\/\//, "");
    const content = children ?? fallbackText;

    return (
        <Link isExternal={isExternal} href={normalizedHref} {...linkProps}>
            <u>{content}</u>
            {isExternal && (
                <>
                    <span> </span>
                    <ExternalLinkIcon />
                </>
            )}
        </Link>
    );
}

const ProjectCard = (props) => {

    const Context = props.context;
    const [videoHeight, setVideoHeight] = useState(0);

    const observer = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {

        const calculateFontSize = () => {
            if (videoRef.current) {
                setVideoHeight(videoRef.current.offsetHeight);
            }
        };

        observer.current = new ResizeObserver(calculateFontSize);
        const currentVideoRef = videoRef.current;

        if (currentVideoRef) {
            observer.current.observe(currentVideoRef);
        }

        calculateFontSize();

        return () => {
            if (observer.current && currentVideoRef) {
                observer.current.unobserve(currentVideoRef);
            }
        };
    }, [videoRef]);

    return (
        <>
            {props.expanded ?
                // ↓ Expanded box ↓
                <MotionBox
                    layoutId={`${props.layoutId}`}
                    position="fixed"
                    zIndex={1}
                    top="80px"
                    bottom="40px"
                    left="18px"
                    right="18px"
                    maxWidth="990px"
                    maxHeight="770px"
                    margin="auto"
                    background={props.themeColor}
                    // backgroundSize="auto"
                    overflow="scroll"
                    boxShadow="lg"
                    borderWidth="1px"
                    borderRadius="12px"
                    exit={{ opacity: 1 }} // Does nothing; Just a hack to fire the callback functions
                    onAnimationStart={() => { props.isAnimating.current = true; }}
                    onAnimationComplete={() => {
                        setTimeout(() => {
                            props.isAnimating.current = false;
                        }, 1000);
                    }}
                    transition={{
                        type: "spring",
                        stiffness: 150,
                        damping: 20,
                        mass: 1
                    }}
                >
                    <MotionBox
                        // layoutId={`${props.layoutId}-base`}
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-start"
                        width="100%"
                    >
                        <MotionBox
                            // layoutId={`${props.layoutId}-overlay`}
                            position="fixed" top={0} left={0} right={0} bottom={0}
                            background="rgba(0, 0, 0, 0.0)" zIndex={-2} onClick={() => props.onClick(null)}
                        />
                        <MotionBox display="flex" position="relative" width="100%" height={videoHeight}>
                            <video ref={videoRef} width={"100%"} autoPlay muted loop playsInline poster={props.backgroundImage} style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                zIndex: -1
                            }}>
                                <source src={props.video} type="video/mp4" />
                            </video>
                            <MotionBox
                                // layoutId={`${props.layoutId}-header-text`}
                                display="flex"
                                position="absolute"
                                top={0} left={0} right={0} bottom={0}
                                justifyContent="flex-end"
                                flexDirection="column"
                            >
                                <MotionBox
                                    pt="36px"
                                    px="36px"
                                    background={`linear-gradient(to bottom, ${props.themeColor}00 0%, ${props.themeColor}ff 100%)}`}
                                >
                                    <MotionText
                                        // layoutId={`${props.layoutId}-year`}
                                        fontFamily={props.useProjectF ? "ProjectF-VF" : "Ubuntu"}
                                    >
                                        <b>{props.year}</b></MotionText>
                                    <MotionHeading
                                        // layoutId={`${props.layoutId}-heading`}
                                        textColor="white" fontSize={36} fontFamily={props.useProjectF ? "ProjectF-VF" : "Ubuntu"}>
                                        <b>{props.title}</b>
                                    </MotionHeading>
                                    <MotionText
                                        // layoutId={`${props.layoutId}-description`}
                                        textColor="white" fontFamily={props.useProjectF ? "ProjectF-VF" : "Ubuntu"}>{props.description}
                                    </MotionText>
                                </MotionBox>
                            </MotionBox>
                        </MotionBox>
                        <MotionBox
                            // layoutId={`${props.layoutId}-context`}
                            display="flex"
                            flexDirection="column"
                            padding="36px"
                            rowGap="6px"
                            background={props.themeColor}
                        >
                            <Context />
                        </MotionBox>
                        <IconButton position="absolute" top="36px" right="36px" icon={<CloseIcon />} onClick={() => props.onClick(null)} />
                    </MotionBox>
                </MotionBox>
                // ↑ Expanded box ↑
                :
                // ↓ Collapsed box ↓
                <MotionBox
                    layoutId={`${props.layoutId}`}
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    width="100%"
                    height="100%"
                    background={`url(${props.backgroundImage})`}
                    margin="0"
                    backgroundSize="cover"
                    overflow="hidden"
                    boxShadow="lg"
                    borderWidth="1px"
                    borderRadius="12px"
                    onClick={props.isAnimating.current ? null : () => { props.onClick(props.layoutId); }}
                    whileHover={{ scale: 1.025 }}
                >
                    <MotionBox
                        // layoutId={`${props.layoutId}-header-text`}
                        display="flex"
                        flexDirection="column"
                        pointerEvents="none"
                        padding="36px"
                        background={`linear-gradient(to bottom, ${props.themeColor}ff 0%, ${props.themeColor}90 80%, ${props.themeColor}00 100%)}`}
                    >
                        <MotionHeading
                            // layoutId={`${props.layoutId}-heading`}
                            maxWidth="400px"
                            textColor="white"
                            fontSize={36}
                            fontFamily={props.useProjectF ? "ProjectF-VF" : "Ubuntu"}
                            pointerEvents="none"
                        ><b>{props.title}</b></MotionHeading>
                        <MotionText
                            // layoutId={`${props.layoutId}-description`}
                            textColor="white"
                            fontFamily={props.useProjectF ? "ProjectF-VF" : "Ubuntu"}
                            pointerEvents="none"
                        >{props.description}
                        </MotionText>
                    </MotionBox>
                </MotionBox>
                // ↑ Collapsed box ↑
            }
        </>
    );
};

const AvatarContext = () => {
    return <>
        <MotionText>Hey there! I'm Xun Zhang, a video and board game enthusiast, making a living as a software enginneer.</MotionText>
        <MotionText>This interactive portfolio, as a showcase of my personal projects, contains many random, disconnected, and even mysterious stuff. Hope you find them fascinating to browse! And, please don't panic if you see anything unreadable; nothing is broken here.</MotionText>
        <MotionText>Most of the projects are created with open-source licenses and are available via GitHub or my <StyledLink isExternal href="https://gitlab.shiky.me">GitLab CE server</StyledLink>.</MotionText>
        <MotionText>To swicth to my résumé, click the <DarkMode><IconButton size="xs" icon={<FaFileAlt />} variant="outline" /></DarkMode> button in the top-right corner.</MotionText>
    </>;
};
const PtContext = () => {
    return <>
        <MotionText>
            Inspired by Hideo Kojima's P.T. released in 2014 for PlayStation 4, I made a first person horror game with Unity called "Fake P.T.", in memory of the short-lived masterpiece.
        </MotionText>
        <MotionText>
            It was my very first game with Unity. Despite of the short gameplay time, this game has a full game experience. Besides coding, I also worked on modeling, texture mapping, shader programming, rigging, animation, and audio engineering as the solo developer for this project.
        </MotionText>
        <MotionText>
            The game's source code and assets are now archived in <StyledLink isExternal href="https://gitlab.shiky.me/shiky/fake-playable-teaser-archive">this repository</StyledLink>.
        </MotionText>
    </>;
};
const TaikoContext = () => {
    return <>
        <MotionText>
            This is my open-source project that helps Taiko no Tatsujin enthusiastics make their own taiko drums at home. With this project, you can make a electronic taiko drum and its driver board with a ESP32-S3 microcontroller.
        </MotionText>
        <MotionText>
            Unlike other projects that simulate binary drum input signals as keyboard inputs, my approach provides 1024-level force sensing and a minimal latency of at most 1 millisecond, achieveing the most similar experience with an official arcade game machine.
        </MotionText>
        <MotionText>
            The project is available at <StyledLink isExternal href="https://github.com/ShikyC/Taiko-Drum-Controller-Arduino" /> <Image display="inline-block" alt="GitHub Repo stars" src="https://img.shields.io/github/stars/ShikyC/Taiko-Drum-Controller-Arduino" />.
        </MotionText>
    </>;
};
const BoxContext = () => {
    return <>
        <MotionText>
            As a maker culture lover, I also made a few gadgets to pipeline some repetitive work. One of tools is this <StyledLink isExternal href="https://shiky.me/box" >parametirc box generator</StyledLink>.
        </MotionText>
        <MotionText>
            Organizing board game components have been a persisting hassle to me. A lot of games have hundreds (sometimes thousands) of tokens and cards, and keeping them sorted in plastic bags is far from elegant. To address this, I bought a diode laser cutter and started making boxes with plywood boards. The recurring process of designing small boxes (and shallow trays) was so frustrating, so I made this parametric box generator tool. With this tool, I can easily generate laser cutting schemes and 3D models for printing.
        </MotionText>
    </>;
};
const SplatoonContext = () => {
    return <>
        <MotionText>
            If you're also a dedicated Splatoon 3 player, you may find this calendar feed useful: <StyledLink isExternal href="https://shiky.me/splatoon3" />.
        </MotionText>
        <MotionText>
            Available in 14 languages, this calendar feed service generates dynamic iCal links that can be added to any calendar app, seamlessly integrating the battle and salmon run events (and more) into your daily routine.
        </MotionText>
        <MotionText>
            Splatfest coming soon? Big Run approaching? Want to do some Eggstra Work? Count on me. Never miss any important events! (Just kidding - all servers can go down occasionally.)
        </MotionText>
    </>;
};
const SaraContext = () => {
    return <>
        <MotionText>Paper available at <StyledLink isExternal href="https://www.ifaamas.org/Proceedings/aamas2018/pdfs/p2250.pdf" />.</MotionText>
        <MotionText>
            We developed Sara, a virtual intelligent agent, expanding the boundaries of simple unilateral communication to a more robust,engaging, believable and meaningful interaction. This is achieved by enabling the agent to complement the basic speech input with voice emotion, facial expressions recognition, and smarter natural language parsing.
        </MotionText>
        <MotionText>
            Sara sets a standard for human-machine communication and how artificially intelligent agents can work in a multi-input model of complex signals in order to elicit meaningful and compelling behaviors in more natural interactions. Moreover, as opposed to other smart agents, Sara learns, remembers and proactively communicates with a complex set of coherent behaviors.
        </MotionText>
    </>;
};
const ArContext = () => {
    return <>
        <MotionText>
            This iPad AR application project built with the Qualcomm Vulforia SDK turns a paperback encyclopedia alive.
        </MotionText>
        <MotionText>
            It contains a information template for general encyclopedia entries and an game template for advanced user interactions, and a few demonstration entries such as polar bear, ping-pong, basic algebra, and 3D geometry. I also developed a virtual conversation agent as the guide to using the app.
        </MotionText>
    </>;
};
const FContext = () => {
    const [fWeight, setFWeight] = useState(400);
    const [fItalic, setFItalic] = useState(0);
    return <>
        <MotionText fontFamily="ProjectF-VF">
            <b></b>  
        </MotionText>
        <MotionText fontFamily="ProjectF-VF">
             <b></b>         
        </MotionText>
        <MotionText fontFamily="ProjectF-VF">
             <b></b>   <b></b> <i></i> <i><b></b></i>       
        </MotionText>
        <MotionText fontFamily="ProjectF-VF">
             
        </MotionText>
        <Center><MotionBox display="flex" flexDirection={["column", "row"]} columnGap={12}>
            <MotionText fontSize={48} fontFamily="ProjectF-VF" style={{ fontVariationSettings: `"wght" ${fWeight}, "ital" ${fItalic}` }}>
                
            </MotionText>
            <Grid templateColumns={"50px 150px"} rowGap={1}>
                <MotionText fontFamily="ProjectF-VF"></MotionText>
                <Slider defaultValue={fWeight} min={100} max={900} onChange={(v) => { setFWeight(v); }}>
                    <SliderTrack>
                        <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                </Slider>
                <MotionText fontFamily="ProjectF-VF"></MotionText>
                <Slider defaultValue={fItalic} min={0} max={1} step={0.002} onChange={(v) => { setFItalic(v); }}>
                    <SliderTrack>
                        <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                </Slider>
            </Grid>
        </MotionBox></Center>
        <MotionText fontFamily="ProjectF-VF">
                       
        </MotionText>
        <MotionText fontFamily="ProjectF-VF">
               
        </MotionText>
    </>;
};


const Projects = () => {

    const projectData = {
        "avatar": {
            useProjectF: false,
            year: "",
            title: "About Me",
            description: "Welcome to my space!",
            themeColor: "#b5adcd",
            backgroundImage: imgAvatar,
            video: vidAvatar,
            context: AvatarContext
        },
        "pt": {
            useProjectF: false,
            year: 2015,
            title: "P.T. Fan Game",
            description: "A horror game built with Unity 4.5, inspired by the P.T. game",
            themeColor: "#1d1d1f",
            backgroundImage: imgPt,
            video: vidPt,
            context: PtContext
        },
        "font": {
            useProjectF: true,
            year: "    ",
            title: " ",
            description: "         ",
            themeColor: "#111111",
            backgroundImage: imgF,
            video: vidF,
            context: FContext
        },
        "taiko": {
            useProjectF: false,
            year: 2016,
            title: "Taiko Controller",
            description: "An ESP32-S3 Taiko no Tatsujin drum controller for PC",
            themeColor: "#1a9994",
            backgroundImage: imgTaiko,
            video: vidTaiko,
            context: TaikoContext
        },
        "box": {
            useProjectF: false,
            year: 2022,
            title: "Parametric Box Generator",
            description: "A quick prototyping tool to generate parametiric boxes for laser cutters and 3D printers",
            themeColor: "#494949",
            backgroundImage: imgBox,
            video: vidBox,
            context: BoxContext
        },
        "splatoon": {
            useProjectF: false,
            year: 2023,
            title: "Splatoon 3 Calendar Feed",
            description: "A calendar feed service for Splatoon 3 players",
            themeColor: "#677094",
            backgroundImage: imgSplatoon,
            video: vidSplatoon,
            context: SplatoonContext
        },
        "reseach1": {
            useProjectF: false,
            year: 2017,
            title: "Sara",
            description: "An emotionally aware embodied conversational agent",
            themeColor: "#b1b0b1",
            backgroundImage: imgSara,
            video: vidSara,
            context: SaraContext
        },
        "ar": {
            useProjectF: false,
            year: 2015,
            title: "AR-Pedia",
            description: "An educational AR app that turns the static encyclopedia into interactive scenes",
            themeColor: "#341c0e",
            backgroundImage: imgAR,
            video: null,
            context: ArContext
        }
        // "reseach2": {
        //     useProjectF: false,
        //     year: 2019,
        //     title: "Research Project 2",
        //     description: "Knowledge powered semantically rich environments",
        //     themeColor: "#7752FE",
        //     backgroundImage: bgE,
        //     video: vidPt,
        //     context: <>
        //         <MotionText>
        //             This is a project.
        //         </MotionText>
        //     </>
        // },
        // "reseach3": {
        //     useProjectF: false,
        //     year: 2021,
        //     title: "Research Project 3",
        //     description: "Social distancing policies",
        //     themeColor: "#C08261",
        //     backgroundImage: bgF,
        //     video: vidPt,
        //     context: <>
        //         <MotionText>
        //             This is a project.
        //         </MotionText>
        //     </>
        // },
    }

    const [selectedCard, setSelectedCard] = useState(null);

    const isAnimating = useRef(false);

    const handleCardClick = (cardId) => {
        if (!isAnimating.current) {
            setSelectedCard(cardId);
        }
    };

    return (<>
        <Grid
            templateColumns={["350px", "400px", "400px", "repeat(2, 400px)", "repeat(2, 400px)", "repeat(3, 400px)"]}
            templateRows={["repeat(8, 400px)", "repeat(8, 400px)", "repeat(8, 400px)", "repeat(4, 400px)", "repeat(4, 400px)", "repeat(3, 400px)"]}
            gap="72px"
        >
            {Object.entries(projectData).map(([key, value]) => (
                <ProjectCard
                    key={key} // Useless by the component; Just to surpress the error
                    layoutId={key}
                    useProjectF={value.useProjectF}
                    year={value.year}
                    title={value.title}
                    description={value.description}
                    themeColor={value.themeColor}
                    backgroundImage={value.backgroundImage}
                    isAnimating={isAnimating}
                    onClick={handleCardClick}
                    expanded={false}
                />
            ))}
        </Grid>
        <AnimatePresence>
            {selectedCard && <>
                <ProjectCard
                    layoutId={selectedCard}
                    useProjectF={projectData[selectedCard].useProjectF}
                    year={projectData[selectedCard].year}
                    title={projectData[selectedCard].title}
                    description={projectData[selectedCard].description}
                    context={projectData[selectedCard].context}
                    themeColor={projectData[selectedCard].themeColor}
                    backgroundImage={projectData[selectedCard].backgroundImage}
                    video={projectData[selectedCard].video}
                    isAnimating={isAnimating}
                    onClick={handleCardClick}
                    expanded={true}

                />
            </>}
        </AnimatePresence>
    </>);
}

const Portfolio = () => {

    useEffect(() => {
        document.title = "Xun Zhang's Portfolio";
    }, []);

    return (
        <Flex>
            <Helmet>
                <meta name="description" content={"My (informal) portfolio. Most projects are driven by pure personal interests."} />
            </Helmet>
            <Flex mx={["6px", "12px", "18px", "36px"]} my="72px" direction="column" p={6} rowGap="72px" w="100%" alignItems="center">
                <Center>
                    <Projects />
                </Center>
            </Flex>
            <Flex position="fixed" top="12px" right="12px" columnGap="12px">
                <Tooltip label="Switch to résumé" placement="bottom" hasArrow>
                    <Link as={RouterLink} to="/resume">
                        <IconButton
                            size="md"
                            fontSize="lg"
                            color="current"
                            icon={<FaFileAlt />}>
                        </IconButton></Link>
                </Tooltip>
                <SwitchColorModeButton />
            </Flex>
        </Flex>
    );
}


export default Portfolio;
